import React from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody } from "reactstrap"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import { Avatar } from "../../../components/DashboardComponents/AvatarGroup"
import MediaFallbackImage from "../../../components/Common/MediaFallbackImage"
import { ICONS } from "../../../constants/icons"
import {
  STATUS_LABEL,
  isActionableFlagStatus,
  statusToneKey,
} from "./adminFlagsData"

/**
 * Flag Details modal — Figma (`40007198:77100`).
 */
const FlagDetailsModal = ({
  isOpen,
  onClose,
  flag,
  isLoading = false,
  isUpdating = false,
  onDismiss,
  onRemoveContent,
  onDelete,
  canUpdate = true,
  canDelete = true,
}) => {
  if (!flag && !isLoading) return null

  const handleDismiss = () => {
    if (!flag || isUpdating) return
    onDismiss?.(flag)
  }

  const handleRemoveContent = () => {
    if (!flag || isUpdating) return
    onRemoveContent?.(flag)
  }

  const canAct = Boolean(
    flag && canUpdate && isActionableFlagStatus(flag.status),
  )
  const tone = flag?.statusTone || statusToneKey(flag?.status)

  return (
    <Modal
      isOpen={isOpen}
      toggle={isUpdating ? undefined : onClose}
      centered
      className="amok-modal amok-modal--password admin-flag-details-modal"
      contentClassName="amok-modal__content admin-flag-details-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-flag-details-modal__body">
        <div className="admin-flag-details-modal__intro">
          <h3 className="admin-flag-details-modal__title">Flag Details</h3>
        </div>

        {isLoading || !flag ? (
          <div
            className="admin-flag-details-modal__skeleton"
            aria-busy="true"
            aria-hidden="true"
          >
            <div className="admin-flag-details-modal__skeleton-line" />
            <div className="admin-flag-details-modal__skeleton-line" />
            <div className="admin-flag-details-modal__skeleton-line admin-flag-details-modal__skeleton-line--lg" />
          </div>
        ) : (
          <>
            <div className="admin-flag-details-modal__content-grid">
              <div className="admin-flag-details-modal__row admin-flag-details-modal__row--two">
                <div className="admin-flag-details-modal__field">
                  <span className="admin-flag-details-modal__label">
                    Reported By
                  </span>
                  <div className="admin-flag-details-modal__user">
                    <Avatar
                      src={flag.reporterAvatar}
                      name={flag.reporter}
                      size="sm"
                      className="admin-flag-details-modal__avatar"
                    />
                    <span className="admin-flag-details-modal__value">
                      {flag.reporter}
                    </span>
                  </div>
                </div>
                <div className="admin-flag-details-modal__field">
                  <span className="admin-flag-details-modal__label">
                    Flagged User
                  </span>
                  <div className="admin-flag-details-modal__user">
                    <Avatar
                      src={flag.flaggedUserAvatar}
                      name={flag.flaggedUser}
                      size="sm"
                      className="admin-flag-details-modal__avatar"
                    />
                    <span className="admin-flag-details-modal__value">
                      {flag.flaggedUser}
                    </span>
                  </div>
                </div>
              </div>

              <div className="admin-flag-details-modal__row admin-flag-details-modal__row--two">
                <div className="admin-flag-details-modal__field">
                  <span className="admin-flag-details-modal__label">
                    Flagged Content
                  </span>
                  <span className="admin-flag-details-modal__value">
                    {flag.content}
                  </span>
                </div>
                <div className="admin-flag-details-modal__field">
                  <span className="admin-flag-details-modal__label">
                    Reason
                  </span>
                  <span className="admin-flag-details-modal__value">
                    {flag.reason}
                  </span>
                </div>
              </div>

              <div className="admin-flag-details-modal__row admin-flag-details-modal__row--two">
                <div className="admin-flag-details-modal__field">
                  <span className="admin-flag-details-modal__label">
                    Date Reported
                  </span>
                  <span className="admin-flag-details-modal__value">
                    {flag.date}
                  </span>
                </div>
                <div className="admin-flag-details-modal__field">
                  <span className="admin-flag-details-modal__label">
                    Status
                  </span>
                  <span
                    className={`admin-flag-details-modal__status admin-flag-details-modal__status--${tone}`}
                  >
                    {STATUS_LABEL[flag.status] || flag.status}
                  </span>
                </div>
              </div>

              <div className="admin-flag-details-modal__description">
                <span className="admin-flag-details-modal__label">
                  Description
                </span>
                <div className="admin-flag-details-modal__description-box">
                  <p>{flag.description}</p>
                </div>
              </div>

              {flag.media?.length > 0 ? (
                <div className="admin-flag-details-modal__media">
                  <span className="admin-flag-details-modal__label">Media</span>
                  <div className="admin-flag-details-modal__media-list">
                    {flag.media.map(item => (
                      <MediaFallbackImage
                        key={item.id}
                        src={item.thumbnailUrl || item.url}
                        fallback={ICONS.thumbnailPlaceholder}
                        alt=""
                        className="admin-flag-details-modal__media-thumb"
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {flag.conversations?.length > 0 ? (
                <div className="admin-flag-details-modal__conversations">
                  <span className="admin-flag-details-modal__label">
                    Conversations
                  </span>
                  <ul className="admin-flag-details-modal__conversation-list">
                    {flag.conversations.map(item => (
                      <li
                        key={item.id}
                        className="admin-flag-details-modal__conversation-item"
                      >
                        <div className="admin-flag-details-modal__user">
                          <Avatar
                            src={item.senderAvatar}
                            name={item.senderName}
                            size="xs"
                          />
                          <span className="admin-flag-details-modal__value">
                            {item.senderName}
                          </span>
                          <span className="admin-flag-details-modal__meta">
                            {item.createdAtLabel}
                          </span>
                        </div>
                        <p>{item.message || "--"}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="admin-flag-details-modal__actions">
              {canAct ? (
                <>
                  <CustomButton
                    variant="secondary"
                    size="md"
                    type="button"
                    className="admin-flag-details-modal__dismiss-btn"
                    loading={isUpdating}
                    disabled={isUpdating}
                    onClick={handleDismiss}
                  >
                    Dismiss Flag
                  </CustomButton>
                  <CustomButton
                    variant="danger"
                    size="md"
                    type="button"
                    loading={isUpdating}
                    disabled={isUpdating}
                    onClick={handleRemoveContent}
                  >
                    Remove Content
                  </CustomButton>
                </>
              ) : null}
              {canDelete && onDelete ? (
                <CustomButton
                  variant="transparent"
                  size="md"
                  type="button"
                  disabled={isUpdating}
                  onClick={() => onDelete?.(flag)}
                >
                  Delete Flag
                </CustomButton>
              ) : null}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  )
}

FlagDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  flag: PropTypes.shape({
    id: PropTypes.string,
    reporter: PropTypes.string,
    reporterAvatar: PropTypes.string,
    flaggedUser: PropTypes.string,
    flaggedUserAvatar: PropTypes.string,
    content: PropTypes.string,
    reason: PropTypes.string,
    date: PropTypes.string,
    status: PropTypes.string,
    statusTone: PropTypes.string,
    description: PropTypes.string,
    media: PropTypes.array,
    conversations: PropTypes.array,
  }),
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  onDismiss: PropTypes.func,
  onRemoveContent: PropTypes.func,
  onDelete: PropTypes.func,
  canUpdate: PropTypes.bool,
  canDelete: PropTypes.bool,
}

FlagDetailsModal.defaultProps = {
  flag: null,
  isLoading: false,
  isUpdating: false,
  onDismiss: undefined,
  onRemoveContent: undefined,
  onDelete: undefined,
  canUpdate: true,
  canDelete: true,
}

export default FlagDetailsModal
